import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, MapPin, Clock, ChevronDown, ChevronUp, Bell, BellOff, Navigation, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Alert severity colors
const ALERT_COLORS = {
  'Tornado Warning': { bg: '#dc2626', text: '#ffffff', border: '#991b1b' },
  'Tornado Watch': { bg: '#f97316', text: '#ffffff', border: '#c2410c' },
  'Hurricane Warning': { bg: '#7c3aed', text: '#ffffff', border: '#5b21b6' },
  'Hurricane Watch': { bg: '#8b5cf6', text: '#ffffff', border: '#6d28d9' },
  'Severe Thunderstorm Warning': { bg: '#eab308', text: '#1f2937', border: '#ca8a04' },
  'Severe Thunderstorm Watch': { bg: '#fcd34d', text: '#1f2937', border: '#f59e0b' },
  'Flash Flood Warning': { bg: '#059669', text: '#ffffff', border: '#047857' },
  'Flash Flood Watch': { bg: '#10b981', text: '#ffffff', border: '#059669' },
  'Flood Warning': { bg: '#0891b2', text: '#ffffff', border: '#0e7490' },
  'default': { bg: '#ef4444', text: '#ffffff', border: '#b91c1c' }
};

const getAlertColors = (event) => ALERT_COLORS[event] || ALERT_COLORS.default;

export default function WeatherAlertBanner({ onDismiss, showSettings = false }) {
  const [alerts, setAlerts] = useState([]);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);

  // Get user's location
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setLocationError(null);
      },
      (error) => {
        console.error("Geolocation error:", error);
        // Default to Hammond, LA if geolocation fails
        setLocation({ latitude: 30.5044, longitude: -90.4612 });
        setLocationError("Using default location (Hammond, LA)");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // Fetch weather alerts
  const fetchAlerts = useCallback(async () => {
    if (!location) return;
    
    try {
      const res = await fetch(
        `${API}/api/weather/alerts?lat=${location.latitude}&lng=${location.longitude}`
      );
      
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        
        // Store location info
        if (data.location?.city) {
          setLocation(prev => ({
            ...prev,
            city: data.location.city,
            state: data.location.state
          }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch weather alerts:", err);
    } finally {
      setLoading(false);
    }
  }, [location]);

  // Request push notification permission
  const enablePushNotifications = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        // Subscribe to weather alerts with current location
        const token = localStorage.getItem('token');
        if (token && location) {
          await fetch(`${API}/api/weather/subscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              latitude: location.latitude,
              longitude: location.longitude,
              push_enabled: true
            })
          });
        }
      }
    } catch (err) {
      console.error("Push notification error:", err);
    }
  };

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  useEffect(() => {
    if (location?.latitude) {
      fetchAlerts();
      
      // Refresh alerts every 5 minutes
      const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [location?.latitude, location?.longitude, fetchAlerts]);

  // Check for existing notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Don't show if dismissed, loading, or no alerts
  if (dismissed || loading || alerts.length === 0) {
    return null;
  }

  const primaryAlert = alerts[0];
  const colors = getAlertColors(primaryAlert.event);
  const additionalAlerts = alerts.slice(1);

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] shadow-lg"
      style={{ backgroundColor: colors.bg }}
      role="alert"
      aria-live="assertive"
      data-testid="weather-alert-banner"
    >
      {/* Main Alert Banner */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-start justify-between gap-4">
          {/* Alert Icon & Content */}
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle 
              className="h-6 w-6 flex-shrink-0 animate-pulse" 
              style={{ color: colors.text }}
            />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 
                  className="text-lg font-bold"
                  style={{ color: colors.text }}
                  data-testid="alert-event-name"
                >
                  {primaryAlert.event}
                </h2>
                {location?.city && (
                  <span 
                    className="text-sm flex items-center gap-1 opacity-90"
                    style={{ color: colors.text }}
                  >
                    <MapPin className="h-3 w-3" />
                    {location.city}, {location.state}
                  </span>
                )}
              </div>
              
              <p 
                className="text-sm mt-1 line-clamp-2"
                style={{ color: colors.text, opacity: 0.95 }}
              >
                {primaryAlert.headline}
              </p>
              
              {primaryAlert.expires && (
                <p 
                  className="text-xs mt-1 flex items-center gap-1"
                  style={{ color: colors.text, opacity: 0.8 }}
                >
                  <Clock className="h-3 w-3" />
                  Expires: {new Date(primaryAlert.expires).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Expand/Collapse */}
            {(additionalAlerts.length > 0 || primaryAlert.description) && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-2 rounded-lg transition-all hover:bg-black/10"
                style={{ color: colors.text }}
                aria-label={expanded ? "Collapse details" : "Expand details"}
                data-testid="expand-alerts-btn"
              >
                {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
            )}

            {/* Settings Link */}
            {showSettings && (
              <a
                href="/alerts"
                className="p-2 rounded-lg transition-all hover:bg-black/10"
                style={{ color: colors.text }}
                title="Weather Alert Settings"
                data-testid="alert-settings-btn"
              >
                <Settings className="h-5 w-5" />
              </a>
            )}

            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="p-2 rounded-lg transition-all hover:bg-black/10"
              style={{ color: colors.text }}
              aria-label="Dismiss alert"
              data-testid="dismiss-alert-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div 
            className="mt-4 pt-4 space-y-4"
            style={{ borderTop: `1px solid ${colors.text}33` }}
          >
            {/* Primary Alert Details */}
            {primaryAlert.description && (
              <div 
                className="text-sm rounded-lg p-3"
                style={{ backgroundColor: `${colors.text}15`, color: colors.text }}
              >
                <h3 className="font-semibold mb-2">Details</h3>
                <p className="whitespace-pre-line text-sm opacity-90">
                  {primaryAlert.description.slice(0, 500)}
                  {primaryAlert.description.length > 500 && '...'}
                </p>
                
                {primaryAlert.instruction && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.text}20` }}>
                    <h4 className="font-semibold mb-1">Instructions</h4>
                    <p className="text-sm opacity-90">{primaryAlert.instruction}</p>
                  </div>
                )}
              </div>
            )}

            {/* Additional Alerts */}
            {additionalAlerts.length > 0 && (
              <div>
                <h3 
                  className="text-sm font-semibold mb-2"
                  style={{ color: colors.text }}
                >
                  {additionalAlerts.length} Additional Alert{additionalAlerts.length > 1 ? 's' : ''}
                </h3>
                <div className="space-y-2">
                  {additionalAlerts.map((alert, i) => {
                    const alertColors = getAlertColors(alert.event);
                    return (
                      <div 
                        key={alert.id || i}
                        className="rounded-lg p-3 text-sm"
                        style={{ 
                          backgroundColor: alertColors.bg, 
                          color: alertColors.text,
                          border: `1px solid ${alertColors.border}`
                        }}
                      >
                        <div className="font-semibold">{alert.event}</div>
                        <div className="text-xs opacity-90 mt-1">{alert.headline}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional alerts count badge */}
        {!expanded && additionalAlerts.length > 0 && (
          <button
            onClick={() => setExpanded(true)}
            className="mt-2 text-xs flex items-center gap-1 hover:underline"
            style={{ color: colors.text, opacity: 0.9 }}
          >
            +{additionalAlerts.length} more alert{additionalAlerts.length > 1 ? 's' : ''} in your area
          </button>
        )}
      </div>
    </div>
  );
}
